/**
 * Co-op lobby styling.
 *
 * The menu palette, bone white on near black with amber for anything live, and the same fluid
 * sizing so one layout serves a phone and a desktop. Injected with the lobby and removed with it.
 *
 * The code is the largest thing on the screen on purpose: it is what somebody reads out loud, and
 * a monospaced face with wide tracking is what makes eight characters survive being read badly.
 */

import { injectCss } from '@/ui/cssInjector';

const CSS = `
.lobby {
  position: fixed;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: clamp(14px, 2.6vh, 26px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding:
    calc(env(safe-area-inset-top, 0px) + clamp(24px, 6vh, 64px))
    calc(env(safe-area-inset-right, 0px) + clamp(20px, 5vw, 72px))
    calc(env(safe-area-inset-bottom, 0px) + clamp(24px, 5vh, 56px))
    calc(env(safe-area-inset-left, 0px) + clamp(20px, 5vw, 72px));
  background: linear-gradient(160deg, #0b0c0e 0%, #101215 60%, #08090b 100%);
  font: 500 16px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: #f2ede3;
  -webkit-tap-highlight-color: transparent;
}

.lobby-title {
  margin: 0;
  font-size: clamp(28px, 6vw, 52px);
  letter-spacing: 0.04em;
}

.lobby-strap {
  margin: 0;
  max-width: 62ch;
  color: rgba(242, 237, 227, 0.66);
}

.lobby-status {
  margin: 0;
  padding: 12px 16px;
  border-left: 3px solid #e8b34a;
  background: rgba(232, 179, 74, 0.08);
}

.lobby-code {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 18px;
}

.lobby-code-label {
  font-size: 13px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(242, 237, 227, 0.55);
}

.lobby-code-value {
  font-size: clamp(30px, 8vw, 56px);
  letter-spacing: 0.14em;
  color: #e8b34a;
}

.lobby-advice, .lobby-warning {
  margin: 0;
  max-width: 62ch;
  font-size: 14px;
  color: rgba(242, 237, 227, 0.72);
}

.lobby-warning { color: #e8b34a; }

/* Bounded, and it says so: a countdown is what makes a player wait instead of reloading the tab. */
.lobby-reconnect {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
  align-items: center;
  padding: 14px 18px;
  border: 1px solid rgba(232, 179, 74, 0.45);
  border-radius: 6px;
  background: rgba(232, 179, 74, 0.08);
}

.lobby-reconnect-line { margin: 0; flex: 1 1 260px; }

.lobby-action, .lobby-quiet, .lobby-copy {
  min-height: 48px;
  padding: 0 20px;
  border: 1px solid rgba(242, 237, 227, 0.24);
  border-radius: 4px;
  background: rgba(242, 237, 227, 0.06);
  color: #f2ede3;
  font: inherit;
  cursor: pointer;
}

.lobby-action { border-color: #e8b34a; color: #e8b34a; }
.lobby-copy { min-height: 40px; padding: 0 14px; font-size: 14px; }
.lobby-action:hover,
.lobby-quiet:hover,
.lobby-copy:hover { background: rgba(242, 237, 227, 0.12); }

.lobby-members {
  display: grid;
  gap: 8px;
  max-width: 720px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.lobby-member {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid rgba(242, 237, 227, 0.14);
  border-radius: 4px;
  background: rgba(242, 237, 227, 0.04);
}

.lobby-member-self { border-color: rgba(232, 179, 74, 0.5); }
/* A seat being kept reads as absent rather than as broken: they are expected back. */
.lobby-member-held { opacity: 0.6; }

.lobby-member-name { font-size: 18px; }
.lobby-member-tags { display: flex; flex-wrap: wrap; gap: 6px; }

.lobby-tag {
  padding: 3px 9px;
  border: 1px solid rgba(242, 237, 227, 0.2);
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(242, 237, 227, 0.66);
}

.lobby-tag-host, .lobby-tag-self { border-color: rgba(232, 179, 74, 0.5); color: #e8b34a; }
.lobby-tag-ready { border-color: rgba(126, 200, 130, 0.55); color: #7ec882; }
.lobby-tag-held { border-color: rgba(232, 179, 74, 0.4); color: #e8b34a; }
/* The measured half of a link, beside the path chip that says which way it got there. */
.lobby-tag-band-good { border-color: rgba(126, 200, 130, 0.5); color: #7ec882; }
.lobby-tag-band-fair { border-color: rgba(232, 179, 74, 0.45); color: #e8b34a; }
.lobby-tag-band-poor { border-color: rgba(214, 106, 90, 0.55); color: #d66a5a; }

.lobby-rules {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 14px;
}
.lobby-rules-label {
  font-size: 12px;
  letter-spacing: 1px;
  opacity: 0.6;
  text-transform: uppercase;
}
.lobby-rules-read {
  font-size: 13px;
  opacity: 0.85;
}
.lobby-rules-toggle {
  min-width: 64px;
}

.lobby-exits { display: flex; gap: 12px; margin-top: auto; }

.lobby .is-hidden { display: none; }
`;

export function injectLobbyStyles(): () => void {
  return injectCss(CSS);
}
